import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthDispatchContext, AuthStateContext, SET_USER_DETAIL } from '@percent/cause-dashboard/context/auth'
import { ServiceContext } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { logger } from '@percent/cause-dashboard/common/utility/logger'
import { useLocalUserPreference } from '@percent/cause-dashboard/common/hooks/useLocalUserPreference/useLocalUserPreference'
import { DEFAULT_LOCALE, supportedLocales } from '@percent/cause-dashboard/translations/supportedLocales'

import type { SupportedLocale } from '@percent/cause-dashboard/translations/supportedLocales'

export {
  DEFAULT_LOCALE,
  fullyTranslatedLocales,
  supportedLocales,
  LOCALE_LABELS
} from '@percent/cause-dashboard/translations/supportedLocales'
export type { SupportedLocale } from '@percent/cause-dashboard/translations/supportedLocales'

const isSupported = (locale: string): locale is SupportedLocale =>
  (supportedLocales as readonly string[]).includes(locale)

const matchSupported = (locale: string | null | undefined): SupportedLocale | null => {
  if (!locale) return null
  if (isSupported(locale)) return locale
  const prefix = locale.split('-')[0].toLowerCase()
  return supportedLocales.find(l => l.split('-')[0].toLowerCase() === prefix) ?? null
}

const detectFromBrowser = (): SupportedLocale | null =>
  typeof navigator !== 'undefined' ? matchSupported(navigator.language) : null

export const useLocale = () => {
  const { i18n } = useTranslation()
  const [stored, setStored] = useLocalUserPreference('locale')

  // Read auth/services via context directly (rather than the throwing
  // useAuthState/useServices hooks) so useLocale stays usable in provider-less
  // render contexts — it is reached transitively (e.g. via LocalePicker in
  // shared layout) by components whose tests render without these providers.
  const authStateContext = useContext(AuthStateContext)
  const authDispatchContext = useContext(AuthDispatchContext)
  const serviceContext = useContext(ServiceContext)

  const user = authStateContext?.authState.user

  const locale: SupportedLocale =
    matchSupported(user?.preferredLanguage) ?? matchSupported(stored) ?? detectFromBrowser() ?? DEFAULT_LOCALE

  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale)
    }
  }, [locale, i18n])

  const setLocale = (next: SupportedLocale) => {
    setStored(next)

    // Updating the account is verified-only, and the 401 it gets otherwise expires the session, so an
    // unverified user's choice is only applied locally.
    if (user?.id && !user.verifiedAt && authDispatchContext) {
      authDispatchContext.authDispatch({
        type: SET_USER_DETAIL,
        payload: { user: { ...user, preferredLanguage: next } }
      })
      return
    }

    if (user?.id && serviceContext && authDispatchContext) {
      serviceContext.accountService
        .updateAccount({ preferredLanguage: next })
        .then(({ data: { data } }) => {
          authDispatchContext.authDispatch({ type: SET_USER_DETAIL, payload: { user: { ...user, ...data } } })
        })
        .catch(error => {
          logger(error as Error)
        })
    }
  }

  return { locale, setLocale, supportedLocales }
}
